// Create or reschedule a booking. Shared by BookingsPage, BookingDetailPage and
// the lead-detail "Scheduled Calls" section.
//
// mode="create": admin books on behalf of a lead (any time, off-hours OK). The
//   backend does NOT send a confirmation SMS/email for admin-created calls.
// mode="edit": reschedule + edit contact/notes of an existing booking.
//
// A lead search lets the admin link the booking to an existing CRM lead (so data
// isn't typed wrong); leaving it unlinked is allowed for brand-new contacts.
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaInfoCircle, FaSearch, FaUserCheck, FaTimes, FaSpinner } from 'react-icons/fa';
import Modal from '../../../components/shared/Modal';
import formatPhone from '../../../components/shared/formatPhone';
import { createCall, updateCall } from '../callScheduleService';
import { searchLeads } from '../../leads/leadService';

const Field = styled.div`
  margin-bottom: 14px;
  label { display: block; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: ${p => p.theme.colors.textSecondary}; margin-bottom: 6px; }
  input, textarea {
    width: 100%; padding: 9px 12px; font-family: inherit; font-size: 0.9rem;
    background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
    border: 1.5px solid ${p => p.theme.colors.inputGray}44; border-radius: ${p => p.theme.borderRadius};
    box-sizing: border-box;
  }
  textarea { min-height: 70px; resize: vertical; }
  .react-datepicker-wrapper { width: 100%; }
`;

const SearchField = styled(Field)`
  position: relative;
  .search-input { display: flex; align-items: center; gap: 8px; padding: 9px 12px;
    background: ${p => p.theme.colors.background}; border: 1.5px solid ${p => p.theme.colors.inputGray}44;
    border-radius: ${p => p.theme.borderRadius}; }
  .search-input svg { color: ${p => p.theme.colors.textSecondary}; flex-shrink: 0; }
  .search-input input { border: none; padding: 0; background: transparent; }
`;

const Results = styled.div`
  margin-top: 6px; border: 1px solid ${p => p.theme.colors.inputGray}44; border-radius: ${p => p.theme.borderRadius};
  background: ${p => p.theme.colors.surface}; max-height: 200px; overflow-y: auto;
`;

const ResultItem = styled.button`
  display: block; width: 100%; text-align: left; background: transparent; border: none;
  padding: 9px 12px; cursor: pointer; border-bottom: 1px solid ${p => p.theme.colors.inputGray}22;
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.colors.primary}18; }
  .name { color: ${p => p.theme.colors.textPrimary}; font-size: 0.88rem; font-weight: 500; }
  .meta { color: ${p => p.theme.colors.textSecondary}; font-size: 0.78rem; margin-top: 2px; }
`;

const NoResults = styled.div`padding: 9px 12px; color: ${p => p.theme.colors.textSecondary}; font-size: 0.82rem;`;

const LinkedChip = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: ${p => p.theme.borderRadius};
  background: ${p => p.theme.colors.success}1a; border: 1px solid ${p => p.theme.colors.success}55;
  .info { flex: 1; }
  .name { color: ${p => p.theme.colors.textPrimary}; font-weight: 600; font-size: 0.9rem; display: flex; align-items: center; gap: 7px; }
  .name svg { color: ${p => p.theme.colors.success}; }
  .meta { color: ${p => p.theme.colors.textSecondary}; font-size: 0.78rem; margin-top: 2px; }
  .clear { background: transparent; border: none; color: ${p => p.theme.colors.textSecondary}; cursor: pointer; padding: 4px; }
  .clear:hover { color: ${p => p.theme.colors.error}; }
`;

const Note = styled.div`
  display: flex; gap: 8px; align-items: flex-start;
  background: ${p => p.theme.colors.primary}14;
  color: ${p => p.theme.colors.textSecondary};
  border-radius: ${p => p.theme.borderRadius};
  padding: 9px 12px; font-size: 0.78rem; line-height: 1.4; margin-bottom: 14px;
  svg { color: ${p => p.theme.colors.primary}; flex-shrink: 0; margin-top: 2px; }
`;

const ErrorText = styled.div`color: ${p => p.theme.colors.error}; font-size: 0.82rem; margin-bottom: 10px;`;

const emptyForm = { name: '', email: '', phone: '', notes: '' };

const BookingModal = ({ isOpen, mode = 'create', booking = null, prefill = null, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [slot, setSlot] = useState(null);
  const [linkedLead, setLinkedLead] = useState(null); // { id, name, email, phone }
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = not searched; [] = no matches
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const searchSeq = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    setError(''); setQuery(''); setResults(null);
    if (mode === 'edit' && booking) {
      setForm({ name: booking.name || '', email: booking.email || '', phone: booking.phone || '', notes: booking.notes || '' });
      setSlot(booking.slot_start ? new Date(booking.slot_start) : null);
      setLinkedLead(
        booking.lead_id
          ? (booking.lead || { id: booking.lead_id, name: booking.name, email: booking.email, phone: booking.phone })
          : null
      );
    } else {
      setForm({ name: prefill?.name || '', email: prefill?.email || '', phone: prefill?.phone || '', notes: '' });
      setSlot(null);
      setLinkedLead(prefill?.lead_id ? { id: prefill.lead_id, name: prefill.name, email: prefill.email, phone: prefill.phone } : null);
    }
  }, [isOpen, mode, booking, prefill]);

  // Debounced lead search.
  useEffect(() => {
    if (!isOpen || linkedLead) return;
    const q = query.trim();
    if (q.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const seq = ++searchSeq.current;
    const t = setTimeout(async () => {
      try {
        const res = await searchLeads(q);
        if (seq !== searchSeq.current) return; // stale
        const list = Array.isArray(res.data) ? res.data : (res.data?.leads || []);
        setResults(list.slice(0, 8));
      } catch (err) {
        if (seq === searchSeq.current) setResults([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, isOpen, linkedLead]);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const pickLead = (lead) => {
    setLinkedLead({ id: lead.id, name: lead.name, email: lead.email, phone: lead.phone });
    setForm({ name: lead.name || '', email: lead.email || '', phone: lead.phone || '', notes: form.notes });
    setQuery(''); setResults(null);
  };

  const clearLink = () => setLinkedLead(null);

  const handleSave = async () => {
    if (!slot || isNaN(slot.getTime())) { setError('Pick a date and time for the call.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        slot_start: slot.toISOString(),
        lead_id: linkedLead?.id ?? null,
      };
      if (mode === 'edit' && booking) {
        await updateCall(booking.id, payload);
        onSaved?.('Booking updated');
      } else {
        await createCall(payload);
        onSaved?.('Call booked');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the booking. Please try again.');
      setSaving(false);
      return;
    }
    setSaving(false);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={mode === 'edit' ? 'Reschedule / Edit Booking' : 'Book a Call'}
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={handleSave}
    >
      {mode === 'create' && (
        <Note>
          <FaInfoCircle />
          <span>Admin-booked calls are saved without sending the lead a confirmation text or email. You can book any time, including outside business hours.</span>
        </Note>
      )}

      {error && <ErrorText>{error}</ErrorText>}

      {/* Lead linkage: search & select an existing lead, or leave unlinked. */}
      {linkedLead ? (
        <Field>
          <label>Linked lead</label>
          <LinkedChip>
            <div className="info">
              <div className="name"><FaUserCheck /> {linkedLead.name || 'Lead #' + linkedLead.id}</div>
              <div className="meta">{[linkedLead.phone && formatPhone(linkedLead.phone), linkedLead.email].filter(Boolean).join(' · ') || 'No contact info'}</div>
            </div>
            <button type="button" className="clear" title="Unlink" onClick={clearLink}><FaTimes /></button>
          </LinkedChip>
        </Field>
      ) : (
        <SearchField>
          <label>Find lead (optional)</label>
          <div className="search-input">
            {searching ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSearch />}
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, or phone…"
            />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
          {results !== null && (
            <Results>
              {results.length === 0 ? (
                <NoResults>No leads found — fill the fields below to book without a link.</NoResults>
              ) : results.map(lead => (
                <ResultItem key={lead.id} type="button" onClick={() => pickLead(lead)}>
                  <div className="name">{lead.name || 'Lead #' + lead.id}</div>
                  <div className="meta">{[lead.phone && formatPhone(lead.phone), lead.email].filter(Boolean).join(' · ')}</div>
                </ResultItem>
              ))}
            </Results>
          )}
        </SearchField>
      )}

      <Field>
        <label>Date &amp; Time (Eastern)</label>
        <DatePicker
          selected={slot}
          onChange={(d) => setSlot(d)}
          showTimeSelect
          timeIntervals={15}
          dateFormat="EEE, MMM d, yyyy h:mm aa"
          placeholderText="Select date & time"
        />
      </Field>

      <Field>
        <label>Name</label>
        <input value={form.name} onChange={set('name')} placeholder="Lead name" />
      </Field>

      <Field>
        <label>Email</label>
        <input value={form.email} onChange={set('email')} placeholder="email@example.com" />
      </Field>

      <Field>
        <label>Phone</label>
        <input value={form.phone} onChange={set('phone')} placeholder="(508) 000-0000" />
      </Field>

      <Field>
        <label>Notes</label>
        <textarea value={form.notes} onChange={set('notes')} placeholder="Optional notes for this call" />
      </Field>

      {saving && <div style={{ color: '#F69620', fontSize: '0.84rem' }}>Saving…</div>}
    </Modal>
  );
};

export default BookingModal;
